import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as legalEntity_annotationServiceGetGetAccessGroupAnnotation } from '@festina/legalentity-general-types/legalentity/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import type {
  AccessGroupAnnotation,
  BusinessType,
} from '@festina/life-general-types/life'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as life_annotationServiceGetGetAccessGroupAnnotation } from '@festina/life-general-types/life/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as moneyIn_annotationServiceGetGetAccessGroupAnnotation } from '@festina/moneyin-general-types/moneyin/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as moneyOut_annotationServiceGetGetAccessGroupAnnotation } from '@festina/moneyout-general-types/moneyout/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { AnnotationsService_annotationServiceGetGetAccessGroupAnnotation as unitLink_annotationServiceGetGetAccessGroupAnnotation } from '@festina/unitlink-general-types/unitlink/hooks/AnnotationsService_annotationServiceGetGetAccessGroupAnnotation'
import { useIsUserAllowedToAccessRoles } from '@festina-life/authentication'
import { notEmpty } from '@festina-life/core'

import { useHook, useService } from '../../lenteHooks'
import { useAvailableComponents } from '../useAvailableComponents'

export type AnnotationProps =
  | {
      overwriteInternalId?: string
      businessType: BusinessType
      enabled?: never
    }
  | {
      overwriteInternalId?: never
      businessType?: never
      enabled: boolean
    }

const getAnnotationQueryParams = ({
  canFetch,
  tenantId,
  ownerId,
  businessType,
}: {
  canFetch: boolean
  tenantId?: string
  businessType?: string
  ownerId?: string
}) =>
  canFetch && tenantId && ownerId && businessType
    ? { tenantId, secondaryId: ownerId, businessType }
    : null

const selectAnnotations = <T extends AccessGroupAnnotation | null>(data: T) =>
  data?.groups?.filter(notEmpty) ?? []

export const useAnnotations = ({
  overwriteInternalId,
  businessType,
  enabled = true,
  shouldSkipSpecificFetches = true,
}: AnnotationProps & {
  shouldSkipSpecificFetches?: boolean
}) => {
  const {
    isUsingLife: isUsingLifeComponent,
    isUsingLegalEntity: isUsingLegalEntityComponent,
    isUsingMoneyIn: isUsingMoneyInComponent,
    isUsingMoneyOut: isUsingMoneyOutComponent,
    isUsingUnitLink: isUsingUnitLinkComponent,
  } = useAvailableComponents()
  const isAnnotationEnabled = useService(
    'featureFlags.annotation.isAnnotationEnabled',
  )

  // For now we'll only do a single service. When we have a query service we will change this code...
  const isUsingLife =
    !shouldSkipSpecificFetches && isUsingLifeComponent && isAnnotationEnabled
  const isUsingLegalEntity = isUsingLegalEntityComponent && isAnnotationEnabled
  const isUsingMoneyIn =
    !shouldSkipSpecificFetches && isUsingMoneyInComponent && isAnnotationEnabled
  const isUsingMoneyOut =
    !shouldSkipSpecificFetches &&
    isUsingMoneyOutComponent &&
    isAnnotationEnabled
  const isUsingUnitLink =
    !shouldSkipSpecificFetches &&
    isUsingUnitLinkComponent &&
    isAnnotationEnabled

  const { legalEntityId: routeOwnerId, tenantId } = useHook(
    'routing.general.useLegalEntityParams',
  )({
    isOptional: true,
  })

  const ownerId = overwriteInternalId ?? routeOwnerId
  const canAccessRoles = useIsUserAllowedToAccessRoles()

  const legalEntity = legalEntity_annotationServiceGetGetAccessGroupAnnotation
  const canFetchLegalEntityAnnotations =
    isUsingLegalEntity &&
    !!ownerId &&
    canAccessRoles({
      component: 'legalEntity',
      rolesToCheck: legalEntity.roles,
    })
  const { data: leData } = legalEntity.useSuspenseQuery(
    getAnnotationQueryParams({
      canFetch: canFetchLegalEntityAnnotations,
      tenantId,
      ownerId,
      businessType,
    }),
    {
      enabled: canFetchLegalEntityAnnotations && enabled,
      select: selectAnnotations,
    },
  )

  const life = life_annotationServiceGetGetAccessGroupAnnotation
  const canFetchLifeAnnotations =
    isUsingLife &&
    !!ownerId &&
    canAccessRoles({ component: 'life', rolesToCheck: life.roles })
  const { data: lifeData } = life.useSuspenseQuery(
    getAnnotationQueryParams({
      canFetch: canFetchLifeAnnotations,
      tenantId,
      ownerId,
      businessType,
    }),
    { enabled: canFetchLifeAnnotations && enabled, select: selectAnnotations },
  )
  const moneyIn = moneyIn_annotationServiceGetGetAccessGroupAnnotation
  const canFetchMoneyInAnnotations =
    isUsingMoneyIn &&
    !!ownerId &&
    canAccessRoles({ component: 'moneyIn', rolesToCheck: moneyIn.roles })
  const { data: moneyInData } = moneyIn.useSuspenseQuery(
    getAnnotationQueryParams({
      canFetch: canFetchMoneyInAnnotations,
      tenantId,
      ownerId,
      businessType,
    }),
    {
      enabled: canFetchMoneyInAnnotations && enabled,
      select: selectAnnotations,
    },
  )
  const moneyOut = moneyOut_annotationServiceGetGetAccessGroupAnnotation
  const canFetchMoneyOutAnnotations =
    isUsingMoneyOut &&
    !!ownerId &&
    canAccessRoles({ component: 'moneyOut', rolesToCheck: moneyOut.roles })
  const { data: moneyOutData } = moneyOut.useSuspenseQuery(
    getAnnotationQueryParams({
      canFetch: canFetchMoneyOutAnnotations,
      tenantId,
      ownerId,
      businessType,
    }),
    {
      enabled: canFetchMoneyOutAnnotations && enabled,
      select: selectAnnotations,
    },
  )
  const unitLink = unitLink_annotationServiceGetGetAccessGroupAnnotation
  const canFetchUnitLinkAnnotations =
    isUsingUnitLink &&
    !!ownerId &&
    canAccessRoles({ component: 'unitLink', rolesToCheck: unitLink.roles })
  const { data: unitLinkData } = unitLink.useSuspenseQuery(
    getAnnotationQueryParams({
      canFetch: canFetchUnitLinkAnnotations,
      tenantId,
      ownerId,
      businessType,
    }),
    {
      enabled: canFetchUnitLinkAnnotations && enabled,
      select: selectAnnotations,
    },
  )

  return {
    legalentityAnnotations: leData,
    lifeAnnotations: lifeData,
    moneyInAnnotations: moneyInData,
    moneyOutAnnotations: moneyOutData,
    unitLinkAnnotations: unitLinkData,
  }
}
